import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { useTheme } from 'styled-components';
import { Assets } from "../../assets";
import { Close } from "../../assets/Close";
import { ChildrenContainer, ClearButton, DropdownPositioner, Container, Gutter, HelpMessage, IconContainer, Input, InputContainer, Label } from "./Combobox.styles";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 *  A Combobox component. It is a input with a dropdown menu.
 *
 * @param clearable If the input is clearable
 * @param disabled If the input is disabled
 * @param error If the input has an error
 * @param fullWidth If the input is full width
 * @param helpGutter If the help message has a gutter
 * @param helpMessage Help message to be displayed
 * @param label Label to be displayed
 * @param leftIcon Icon to be displayed on the left side of the input
 * @param maxItemRows Maximum number of rows to be displayed
 * @param name Name of the input
 * @param rightIcon Icon to be displayed on the right side of the input
 * @param size Size of the input. It can be sm, md or lg
 * @param value Value of the input
 * @param onSelectItem Callback function to be called when an item is selected
 */
const Combobox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    name,
    clearable = false,
    disabled,
    error,
    helpGutter = true,
    helpMessage,
    label,
    leftIcon,
    maxItemRows = 4,
    placeholder,
    rightIcon,
    size = 'md',
    value,
    children,
    onBlur,
    onChange,
    onFocus,
    onSelectItem,
    ...rest
  } = props;
  const [isFocused, setIsFocused] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const theme = useTheme();
  const elemId = useRef(nanoid(10));
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const selectItems = Array.isArray(children) ? children : [children];
  const LeftIcon = leftIcon && Assets[leftIcon];
  const RightIcon = rightIcon && Assets[rightIcon];
  useImperativeHandle(ref, () => inputRef.current);
  const handleChange = useCallback(e => {
    setIsDropdownOpen(true);
    if (ref) {
      setFilterValue(e.target.value);
    } else {
      onChange?.(e);
    }
  }, []);
  const handleFocus = useCallback(e => {
    setIsDropdownOpen(true);
    setIsFocused(true);
    onFocus?.(e);
  }, []);
  const handleBlur = useCallback(e => {
    setIsFocused(false);
    onBlur?.(e);
  }, []);
  const filter = useCallback((label, value) => {
    if (!value) return true;
    const labelLowerCase = label.toLowerCase();
    const valueLowerCase = value.toLowerCase();
    return labelLowerCase.includes(valueLowerCase);
  }, []);
  const handleFilter = useCallback(selectItem => {
    if (ref) {
      return filter(selectItem.props.label, filterValue);
    } else {
      return filter(selectItem.props.label, value);
    }
  }, [value, filterValue]);
  const filteredItems = useMemo(() => selectItems.filter(handleFilter), [selectItems, value, filterValue]);
  const handleSelectItem = useCallback((label, value) => {
    if (disabled) return;
    if (inputRef.current) {
      inputRef.current.value = label;
      onBlur?.({
        target: {
          name,
          value: label
        }
      });
    }
    onChange?.({
      target: {
        name,
        value: label
      }
    });
    onSelectItem?.(value);
    setIsDropdownOpen(false);
  }, []);
  const handleClear = useCallback(() => {
    if (inputRef.current) {
      setFilterValue('');
      inputRef.current.value = '';
      onBlur?.({
        target: {
          name,
          value: label
        }
      });
    }
    onChange?.({
      target: {
        value: ''
      }
    });
  }, []);
  const boxMaxHeight = useMemo(() => {
    const itemHeight = size === 'sm' ? 40 : size === 'md' ? 48 : 64;
    const maxHeight = maxItemRows * itemHeight;
    return maxHeight;
  }, [maxItemRows, size]);
  useEffect(() => {
    if (!isDropdownOpen) {
      setFocusedIndex(-1);
    }
  }, [isDropdownOpen]);
  useEffect(() => {
    const handleArrowKeys = event => {
      if (!isDropdownOpen) return;
      if (event.key === 'ArrowDown') {
        setFocusedIndex(prevIndex => (prevIndex + 1) % selectItems.length);
      }
      if (event.key === 'ArrowUp') {
        setFocusedIndex(prevIndex => (prevIndex - 1 + selectItems.length) % selectItems.length);
      }
      if (event.key === 'Enter') {
        const focusedItem = selectItems[focusedIndex];
        if (focusedItem) {
          handleSelectItem(focusedItem.props.label, focusedItem.props.value);
        }
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleArrowKeys, true);
    return () => document.removeEventListener('keydown', handleArrowKeys, true);
  }, [isDropdownOpen, focusedIndex]);
  useLayoutEffect(() => {
    if (dropdownRef.current) {
      const focusedItem = dropdownRef.current.children[focusedIndex];
      if (focusedItem) {
        focusedItem?.scrollIntoView?.({
          block: 'nearest'
        });
      }
    }
  }, [focusedIndex]);
  useEffect(() => {
    const input = document.getElementById(elemId.current);
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && input && !input.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [ref, dropdownRef]);
  useEffect(() => {
    const handleEscape = event => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(prev => !prev);
      }
      if (event.key === 'Tab') {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [dropdownRef]);
  return /*#__PURE__*/_jsxs(Container, {
    size: size,
    disabled: disabled,
    children: [label && /*#__PURE__*/_jsx(Label, {
      htmlFor: elemId.current,
      disabled: disabled,
      children: label
    }), /*#__PURE__*/_jsxs(InputContainer, {
      size: size,
      disabled: disabled,
      isFocused: isFocused,
      children: [LeftIcon && /*#__PURE__*/_jsx(IconContainer, {
        children: /*#__PURE__*/_jsx(LeftIcon, {
          size: size === 'lg' ? 'md' : size,
          color: theme.colors.neutralL5
        })
      }), /*#__PURE__*/_jsx(Input, {
        ...rest,
        disabled: disabled,
        id: elemId.current,
        onBlur: handleBlur,
        onChange: handleChange,
        onFocus: handleFocus,
        placeholder: placeholder,
        ref: inputRef,
        tabIndex: 0,
        value: value
      }), RightIcon && /*#__PURE__*/_jsx(IconContainer, {
        children: /*#__PURE__*/_jsx(RightIcon, {
          size: size === 'lg' ? 'md' : size,
          color: theme.colors.neutralL5
        })
      }), clearable && /*#__PURE__*/_jsx(ClearButton, {
        size: size,
        onClick: handleClear,
        children: /*#__PURE__*/_jsx(Close, {
          size: size === 'lg' ? 'md' : size,
          color: theme.colors.secondaryL3
        })
      })]
    }), /*#__PURE__*/_jsx(DropdownPositioner, {
      children: /*#__PURE__*/_jsx(ChildrenContainer, {
        maxHeight: boxMaxHeight,
        open: isDropdownOpen || !!value,
        ref: dropdownRef,
        children: filteredItems.map(item => {
          return /*#__PURE__*/React.cloneElement(item, {
            key: item.props.value,
            size,
            label: item.props.label,
            hightlighted: selectItems.indexOf(item) === focusedIndex,
            onClick: () => handleSelectItem(item.props.label, item.props.value)
          });
        })
      })
    }), helpMessage && /*#__PURE__*/_jsx(HelpMessage, {
      error: error,
      children: helpMessage
    }), helpGutter && /*#__PURE__*/_jsx(Gutter, {})]
  });
});
Combobox.displayName = 'Combobox';
export { Combobox };